import { SignUp, useAuth } from "@clerk/nextjs";
import { useRouter } from "next/compat/router";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { mutate } from "swr";

import { buildOnboardingPayload } from "./buildOnboardingPayload";
import OnboardingWizardStepFooter from "./OnboardingWizardStepFooter";
import OnboardingWizardStepHeader from "./OnboardingWizardStepHeader";
import { setOnboardingPersonProperties } from "./onboardingPersonProperties";
import type { WizardFormValues } from "./onboardingWizardForm";
import useOnboardingTracking from "./useOnboardingTracking";
import { useWizardMode } from "./WizardModeContext";
import { useOnboarding } from "~/hooks";
import {
	getCptStudyPlanPresetNote,
	isCptStudyPlanPresetIntact,
} from "~/lib/cpt-study-plan-preset";
import getSignupUnsafeMetadata from "~/utils/getSignupUnsafeMetadata";

const OnboardingWizardStepAuth = () => {
	const { isSignedIn } = useAuth();
	const { learningPath, handleUpdate } = useOnboarding();
	const { track } = useOnboardingTracking();
	const { preset } = useWizardMode();
	const { getValues } = useFormContext<WizardFormValues>();
	const router = useRouter();
	const [isSaving, setIsSaving] = useState(false);

	const values = getValues();
	const payload = buildOnboardingPayload(values, learningPath);
	const presetNote =
		preset && isCptStudyPlanPresetIntact(values, preset)
			? getCptStudyPlanPresetNote(preset)
			: null;

	// biome-ignore lint/correctness/useExhaustiveDependencies: fire once when the guest signup form is shown
	useEffect(() => {
		if (!isSignedIn) track("onboarding:signup_shown");
	}, [isSignedIn]);

	if (!isSignedIn) {
		const path = router?.asPath.split("?")[0] || "/";

		return (
			<div className="flex h-full min-h-0 flex-1 flex-col gap-4 phablet:gap-6">
				<OnboardingWizardStepHeader />

				<div className="flex min-h-0 flex-1 flex-col items-center gap-3 overflow-y-auto pr-1">
					<h1 className="p-4 text-center font-semibold text-2xl">
						To create your custom learning plan and dashboard, we need to set up
						an account.
					</h1>

					{presetNote && (
						<p className="-mt-2 max-w-md text-center text-slate-600 text-sm dark:text-slate-300">
							{presetNote}
						</p>
					)}

					<SignUp
						routing="hash"
						forceRedirectUrl="/learning-plan?onboarding=processing"
						unsafeMetadata={{
							...getSignupUnsafeMetadata(path),
							onboarding: payload,
						}}
					/>
				</div>
			</div>
		);
	}

	const handleFinish = async () => {
		setIsSaving(true);
		await handleUpdate(payload);
		await mutate("/certifications/selected");
		await mutate("/users/ceu-goals");
		track("onboarding:completed", { source: "deferred_authed" });
		setOnboardingPersonProperties(payload, learningPath);
		router?.push("/learning-plan");
	};

	return (
		<div className="flex h-full min-h-0 flex-1 flex-col gap-4 phablet:gap-6">
			<OnboardingWizardStepHeader
				title="You're all set"
				subtitle="Save your selections and head to your learning plan."
			/>

			<div className="flex min-h-0 flex-1 flex-col">
				{presetNote && (
					<p className="text-slate-600 text-sm dark:text-slate-300">
						{presetNote}
					</p>
				)}
			</div>

			<OnboardingWizardStepFooter
				primary={{
					label: "Finish & View Plan",
					onClick: handleFinish,
					disabled: isSaving,
				}}
			/>
		</div>
	);
};

export default OnboardingWizardStepAuth;
